// DriverSettings.jsx - Fixed Navigation
import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Users, UserPlus, UserX, Calendar, MapPin, Phone, Package, Clock } from "lucide-react";

function DriverSettings() {
  const [driverForm, setDriverForm] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    phone_number: ""
  });
  const [deactivateUsername, setDeactivateUsername] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { action: driverAction } = useParams();

  const goBack = () => navigate("/admin/drivers");

  const handleAddDriver = async () => {
    try {
      const res = await axiosInstance.post("/drivers/create", driverForm);
      alert("✅ Driver added: " + res.data.driver.name);
      setDriverForm({
        name: "",
        email: "",
        username: "",
        password: "",
        phone_number: "",
      });
      goBack();
    } catch (err) {
      console.error("Error adding driver:", err);
      alert("❌ Failed to add driver");
    }
  };

  const handleDeactivateDriver = async () => {
    try {
      const res = await axiosInstance.put(`/drivers/deactivate/${deactivateUsername}`);
      alert("✅ Driver deactivated: " + res.data.driver.name);
      setDeactivateUsername("");
      goBack();
    } catch (err) {
      console.error("Error deactivating driver:", err);
      alert("❌ Failed to deactivate driver");
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/drivers/orders-with-driver/${date}`);
      setOrders(res.data);
    } catch (err) {
      console.error("Error fetching driver report:", err);
      alert("❌ Failed to fetch driver report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (driverAction === "portal") {
      fetchOrders();
    }
  }, [date, driverAction]);

  const getStatusColor = (status) => {
    if (status === "blue") return "bg-gray-300 text-gray-900";
    const now = new Date();
    const order = orders.find(o => o.order_id === status);
    if (!order) return "bg-gray-100 text-gray-700";
    
    const orderDateTime = new Date(`${date}T${order.order_time}Z`);
    const diffMinutes = (now - orderDateTime) / (1000 * 60);
    
    if (diffMinutes < 30) return "bg-green-100 border-green-300 text-green-900";
    if (diffMinutes <= 45) return "bg-yellow-100 border-yellow-300 text-yellow-900";
    return "bg-red-100 border-red-300 text-red-900";
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 md:mb-12">
        <button
          onClick={goBack}
          className="group flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-all font-medium text-sm md:text-base mb-4 md:mb-6"
          style={{ fontFamily: "Bambino" }}
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Drivers</span>
        </button>

        <div className="flex items-center justify-between gap-2">
          <h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent"
            style={{ fontFamily: "Bambino", lineHeight: "1.2" }}
          >
            {driverAction === "portal" && "Driver Portal"}
            {driverAction === "add" && "Add New Driver"}
            {driverAction === "deactivate" && "Deactivate Driver"}
          </h1>
          {driverAction === "add" && <UserPlus className="w-8 h-8 md:w-12 md:h-12 text-blue-500" />}
          {driverAction === "deactivate" && <UserX className="w-8 h-8 md:w-12 md:h-12 text-red-500" />}
          {driverAction === "portal" && <Users className="w-8 h-8 md:w-12 md:h-12 text-cyan-500" />}
        </div>
      </div>

      {/* Add Driver Form */}
      {driverAction === "add" && (
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {[
              { field: "name", label: "Driver Name", icon: Users },
              { field: "email", label: "Email Address", icon: null },
              { field: "username", label: "Username", icon: null },
              { field: "password", label: "Password", icon: null },
              { field: "phone_number", label: "Phone Number", icon: Phone }
            ].map(({ field, label, icon: Icon }) => (
              <div key={field}>
                <label
                  className="block mb-3 font-bold text-gray-700 flex items-center gap-2 text-sm md:text-base"
                  style={{ fontFamily: "Bambino" }}
                >
                  {Icon && <Icon className="w-5 h-5" />}
                  {label}
                </label>
                <input
                  type={field === "password" ? "password" : field === "email" ? "email" : "text"}
                  value={driverForm[field]}
                  onChange={(e) => setDriverForm({ ...driverForm, [field]: e.target.value })}
                  className="w-full border-2 border-blue-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all text-base"
                  placeholder={`Enter ${label.toLowerCase()}`}
                  style={{ fontFamily: "Bambino" }}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={goBack}
              className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-bold text-base md:text-lg"
              style={{ fontFamily: "Bambino" }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddDriver}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl font-bold text-base md:text-lg"
              style={{ fontFamily: "Bambino" }}
            >
              Add Driver
            </button>
          </div>
        </div>
      )}

      {/* Deactivate Driver Form */}
      {driverAction === "deactivate" && (
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-red-100 max-w-md mx-auto">
          <div className="mb-6">
            <label
              className="block mb-3 font-bold text-gray-700 flex items-center gap-2 text-sm md:text-base"
              style={{ fontFamily: "Bambino" }}
            >
              <UserX className="w-5 h-5 text-red-600" />
              Driver Username
            </label>
            <input
              type="text"
              value={deactivateUsername}
              onChange={(e) => setDeactivateUsername(e.target.value)}
              placeholder="Enter username to deactivate"
              className="w-full border-2 border-red-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent transition-all text-base"
              style={{ fontFamily: "Bambino" }}
            />
          </div>

          <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg mb-6">
            <p className="text-sm text-red-800" style={{ fontFamily: "Bambino" }}>
              ⚠️ This action will deactivate the driver and they will no longer have access to the system.
            </p>
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={goBack}
              className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-bold text-base md:text-lg"
              style={{ fontFamily: "Bambino" }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivateDriver}
              className="px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all shadow-lg hover:shadow-xl font-bold text-base md:text-lg"
              style={{ fontFamily: "Bambino" }}
            >
              Deactivate
            </button>
          </div>
        </div>
      )}

      {/* Driver Portal */}
      {driverAction === "portal" && (
        <div className="space-y-6">
          {/* Date Selection */}
          <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-cyan-100">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <div className="flex-1">
                <label
                  className="block mb-3 font-bold text-gray-700 flex items-center gap-2 text-sm md:text-base"
                  style={{ fontFamily: "Bambino" }}
                >
                  <Calendar className="w-5 h-5 text-cyan-600" />
                  Select Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border-2 border-cyan-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:border-transparent transition-all text-base"
                  style={{ fontFamily: "Bambino" }}
                />
              </div>
              <button
                onClick={fetchOrders}
                className="sm:mt-6 px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl font-bold text-base md:text-lg"
                style={{ fontFamily: "Bambino" }}
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Orders Display */}
          {loading ? (
            <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-12 shadow-xl border-2 border-cyan-100 flex flex-col items-center justify-center gap-4">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-4 border-cyan-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-t-cyan-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              </div>
              <span className="text-lg font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>
                Loading orders...
              </span>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-12 shadow-xl border-2 border-cyan-100 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg" style={{ fontFamily: "Bambino" }}>
                No orders found for this date.
              </p>
            </div>
          ) : (
            <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-cyan-100">
              <h2 className="text-xl md:text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                <Clock className="w-6 h-6 text-cyan-600" />
                Active Orders ({orders.length})
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {orders.map((order) => (
                  <button
                    key={order.order_id}
                    onClick={() => setSelectedOrder(order)}
                    className={`p-4 rounded-xl border-2 transition-all hover:shadow-lg transform hover:scale-105 cursor-pointer text-left ${getStatusColor(order.order_id)}`}
                    style={{ fontFamily: "Bambino" }}
                  >
                    <p className="font-bold text-base mb-2">{order.driver_name}</p>
                    <p className="text-sm font-semibold">{order.customer_postal_code}</p>
                    <p className="text-xs opacity-75 mt-1">📍 {order.customer_name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Order Receipt Modal */}
          {selectedOrder && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-cyan-100">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                    <Package className="w-7 h-7 text-cyan-600" />
                    Order Receipt
                  </h2>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="text-gray-400 hover:text-gray-600 text-3xl leading-none font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4" style={{ fontFamily: "Bambino" }}>
                  {/* Customer Details */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-800 mb-3">Customer Details</h3>
                    <p className="text-gray-700"><strong>Name:</strong> {selectedOrder.customer_name}</p>
                    <p className="text-gray-700"><strong>Phone:</strong> {selectedOrder.phone || "N/A"}</p>
                    <p className="text-gray-700 mt-2"><strong>Address:</strong></p>
                    <p className="text-gray-600 ml-4">{selectedOrder.street_address}, {selectedOrder.city}</p>
                    <p className="text-gray-600 ml-4">{selectedOrder.county}, {selectedOrder.postal_code}</p>
                  </div>

                  {/* Driver Details */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-600" />
                      Driver Details
                    </h3>
                    <p className="text-gray-700"><strong>Name:</strong> {selectedOrder.driver_name}</p>
                    <p className="text-gray-700"><strong>Phone:</strong> {selectedOrder.driver_phone}</p>
                  </div>

                  {/* Order Status */}
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-800 mb-3">Order Status</h3>
                    <p className="text-gray-700"><strong>Status:</strong> <span className="px-3 py-1 bg-purple-200 text-purple-900 rounded-lg font-semibold text-sm">{selectedOrder.status}</span></p>
                    <p className="text-gray-700 mt-2"><strong>Order Time:</strong> {selectedOrder.order_time}</p>
                    <p className="text-gray-700"><strong>Total Price:</strong> <span className="text-lg font-bold text-green-600">£{selectedOrder.total_price}</span></p>
                  </div>

                  {/* Items */}
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                    <h3 className="font-bold text-gray-800 mb-3">Order Items</h3>
                    <div className="space-y-2">
                      {selectedOrder.items?.map((item, idx) => (
                        <div key={idx} className="border-l-4 border-yellow-400 pl-3 py-1">
                          <p className="font-semibold text-gray-800">{item.item_name} <span className="text-gray-600">x{item.quantity}</span></p>
                          <p className="text-sm text-gray-600">{item.description}</p>
                          <p className="text-sm font-bold text-green-600">£{item.total_price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedOrder(null)}
                  className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all font-bold text-base"
                  style={{ fontFamily: "Bambino" }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DriverSettings;